import type { HTMLAttributes } from "react";
import { TextField } from "@mui/material";

type FormTextFieldProps = {
  name: string;
  label: string;
  type?: string;
  inputMode?: HTMLAttributes<HTMLElement>["inputMode"];
  value: string | number | readonly string[] | undefined;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  error?: boolean;
  helperText?: string | false;
  disabled?: boolean;
  touched?: Record<string, unknown>;
  errors?: Record<string, unknown>;
};

const FormTextField = ({
  name,
  label,
  value,
  type,
  inputMode,
  onChange,
  error,
  helperText,
  disabled = false,
  touched,
  errors,
}: FormTextFieldProps) => {
  // Compute error props if not explicitly provided and touched/errors are available
  let computedError = false;
  let computedHelper: string | false = "";
  if (typeof error !== "undefined" || typeof helperText !== "undefined") {
    computedError = !!error;
    computedHelper = helperText ?? "";
  } else {
    const isTouched = !!(touched && touched[name as keyof typeof touched]);
    const errVal = errors && (errors[name as keyof typeof errors] as unknown);
    const errMsg = typeof errVal === "string" ? errVal : undefined;
    computedError = !!(isTouched && errMsg);
    computedHelper = isTouched && errMsg ? errMsg : "";
  }

  // Textarea handling: show character counter and enforce 255 max chars
  const isTextArea = type === "textarea";
  const isNumber = type === "number";
  const maxChars = 255;
  const currentLength = isTextArea ? String(value ?? "").length : 0;
  const helperForTextarea =
    isTextArea && !computedError
      ? `${currentLength}/${maxChars}`
      : computedHelper;

  // For number inputs: when empty (undefined/null/''/NaN), show '' instead of 0
  const displayValue = isNumber
    ? value === undefined ||
      value === null ||
      value === "" ||
      (typeof value === "number" && Number.isNaN(value))
      ? ""
      : value
    : value;

  return (
    <TextField
      id="outlined-basic"
      name={name}
      label={label}
      value={displayValue}
      type={type}
      multiline={isTextArea}
      minRows={isTextArea ? 3 : undefined}
      slotProps={{
        input: {
          inputProps: {
            maxLength: isTextArea ? maxChars : undefined,
            inputMode,
          },
        },
        formHelperText: {
          sx: {
            textAlign: "left",
            ml: 0,
            mr: "auto",
            width: "100%",
          },
        },
      }}
      onChange={onChange}
      fullWidth
      size="medium"
      error={computedError}
      helperText={helperForTextarea}
      disabled={disabled}
      sx={
        isNumber
          ? {
              '& input[type="number"]': {
                MozAppearance: "textfield",
              },
              '& input[type="number"]::-webkit-outer-spin-button': {
                WebkitAppearance: "none",
                margin: 0,
              },
              '& input[type="number"]::-webkit-inner-spin-button': {
                WebkitAppearance: "none",
                margin: 0,
              },
            }
          : undefined
      }
    />
  );
};

export default FormTextField;
